import { useState, useEffect } from 'react'
import { Grid, Col, Button, Card, ButtonInput } from 'components/gsys-ui'
import NiceModal from '@ebay/nice-modal-react'
import { UserIcon, KeyIcon, EyeIcon, EyeSlashIcon, TrashIcon, MagnifyingGlassIcon, PlusIcon, PencilSquareIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import haxios, { getToken } from 'util/haxios'
import moment from 'moment'
import { PaginatedTable } from 'components/gsys-ui/Table/Table'

const Users = () => {
  const [companies, setCompanies] = useState([])
  const [viewingCompany, setViewingCompany] = useState(null)
  const [search, setSearch] = useState('')
  const [viewLoading, setViewLoading] = useState(false)

  const refreshList = () => {
    const companiesLs = window.localStorage.getItem('companies')
    const companiesParsed = companiesLs ? JSON.parse(companiesLs) : []
    setCompanies(companiesParsed)
  }

  const removeCompany = (id) => {
    const companiesLs = window.localStorage.getItem('companies')
    const companiesParsed = companiesLs ? JSON.parse(companiesLs) : []
    const companiesFiltered = companiesParsed.filter((company) => company.companyId !== id)
    window.localStorage.setItem('companies', JSON.stringify(companiesFiltered))
    refreshList()
  }

  const handleViewCompany = async (company) => {
    setViewLoading(company.companyId)
    await getToken(company.username, company.password, false)
    setViewingCompany(company)
  }

  useEffect(() => {
    refreshList()
  }, [])

  const companiesFiltered = search ? companies.filter((company) => (
    company.companyName.toLowerCase().includes(search.trim().toLowerCase()) ||
    company.apir.toLowerCase().includes(search.trim().toLowerCase())
  )) : companies

  const companiesSorted = companiesFiltered.sort((a, b) => a.companyName > b.companyName)

  return (
    <Grid gap={7} className="h-full">
      <Col sm={24} md={10} xl={8} xxl={6}>
        <div className="relative h-full">
          <div className="flex absolute inset-0 flex-col">
            <div className="flex flex-none justify-between items-center">
              <Button onClick={() => NiceModal.show('usersAddCompanyModal', { refreshList })} Icon={PlusIcon}>Add company</Button>
              <div className="w-64">
                <ButtonInput
                  placeholder="Find company..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </ButtonInput>
              </div>
            </div>
            <div className="overflow-y-scroll flex-1 mt-2 space-y-2">
              {
                companiesSorted.map((company) => (
                  <CompanyCard
                    company={company}
                    viewCompany={() => handleViewCompany(company)}
                    deleteCompany={() => removeCompany(company.companyId)}
                    isLoading={viewLoading === company.companyId}
                  />
                ))
              }
            </div>
          </div>
        </div>
      </Col>
      <Col sm={24} md={14} xl={16} xxl={18}>
        {
          viewingCompany && <ViewCompany key={viewingCompany.companyId} company={viewingCompany} setLoading={setViewLoading} />
        }
      </Col>
    </Grid>
  )
}

const CompanyCard = ({ company, deleteCompany, viewCompany, isLoading }) => {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <Card className="p-2">
      <div className="flex items-center mb-1">
        <img src={company.logoUrl} className="object-contain mr-2 w-16 h-16 rounded-full border border-gray-400 flex-0" />
        <div className="flex-1">
          <div className="text-2xl font-bold">{company.companyName}</div>
          <div className="text-sm">{company.companyId}</div>
          <div className="text-sm">{company.apir}</div>
        </div>
      </div>
      <div className="mt-2">
        <div className="flex items-center">
          <UserIcon className="mr-1 w-5 h-5" /> {company.username}
        </div>
        <div className="flex items-center">
          <KeyIcon className="mr-1 w-5 h-5" /> <span className="mr-1">{showPassword ? company.password : '•••••••••••••'}</span>
          <Button small rounded variant="white" onClick={() => setShowPassword(!showPassword)}>
            {
              showPassword ? (
                <EyeSlashIcon className="w-5 h-5" />
              ) : (
                <EyeIcon className="w-5 h-5" />
              )
            }
          </Button>
        </div>
      </div>
      <div className="mt-2 space-x-2 text-right">
        <Button small Icon={TrashIcon} variant="white" onClick={() => NiceModal.show('deleteModal', { handleDelete: deleteCompany, name: "company" })}>Remove</Button>
        <Button small Icon={EyeIcon} variant="blue" onClick={viewCompany} loading={isLoading}>View users</Button>
      </div>
    </Card>
  )
}

const ViewCompany = ({ company, setLoading }) => {
  const [users, setUsers] = useState([])
  const [search, setSearch] = useState('')

  const refreshUsers = async () => {
    const usersRes = await haxios.get(`${process.env.REACT_APP_BASE_URL}/api/users/portal`)
    setUsers(usersRes)
    setLoading(false)
  }

  const deleteUser = async (userId) => {
    await haxios.delete(`${process.env.REACT_APP_BASE_URL}/api/users/portal/${userId}`)
    await refreshUsers()
  }

  useEffect(() => {
    refreshUsers()
  }, [company])

  const usersFiltered = search ? users.filter((user) => (
    `${user.FName} ${user.SName}`.toLowerCase().includes(search.trim().toLowerCase()) ||
    user.Username.toLowerCase().includes(search.trim().toLowerCase()) ||
    user.Email.toLowerCase().includes(search.trim().toLowerCase())
  )) : users

  const usersSorted = usersFiltered.sort((a, b) => moment(b.CreateOn).diff(a.CreateOn))

  return (
    <Card>
      <div className="flex justify-between items-center p-2 mb-1">
        <div className="flex items-center">
          <img src={company.logoUrl} className="object-contain mr-2 w-12 h-12 rounded-full border border-gray-400 flex-0" />
          <div className="text-2xl font-bold">{company.companyName}</div>
        </div>
        <div className="flex items-center space-x-2">
          <ButtonInput
            placeholder="Find user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          >
            <MagnifyingGlassIcon className="w-5 h-5" />
          </ButtonInput>
          <Button
            variant="blue"
            onClick={() => NiceModal.show('usersAddUserModal', { refreshList: refreshUsers })}
            Icon={PlusIcon}
          >
            Add user
          </Button>
        </div>
      </div>
      <UsersTable data={usersSorted} deleteUser={deleteUser} refreshUsers={refreshUsers} />
    </Card>
  )
}

const UsersTable = ({ data, deleteUser, refreshUsers }) => {
  return (
    <PaginatedTable
      data={data}
      pageSize={15}
      padToPageSize={true}
      dummyRowHeight={33}
      columns={[
        {
          header: 'NAME',
          id: 'name',
          cell: ({ row }) => `${row.original.FName} ${row.original.SName}`
        },
        {
          header: 'USERNAME',
          accessorKey: 'Username'
        },
        {
          header: 'EMAIL',
          accessorKey: 'Email'
        },
        {
          header: 'CREATED',
          id: 'created',
          cell: ({ row }) => moment(row.original.CreateOn).format('HH:mm DD/MM/YYYY')
        },
        {
          header: 'LAST USED',
          id: 'used',
          cell: ({ row }) => row.original.LastUse ? moment(row.original.LastUse).format('HH:mm DD/MM/YYYY') : 'Never'
        },
        {
          header: '',
          accessorKey: 'edit',
          cellSize: 1,
          disableSortBy: true,
          cellPad: false,
          cell: ({ row }) => (
            <div className="flex items-center px-1 h-full">
              <Button
                variant="rounded"
                tiny
                onClick={() => NiceModal.show('usersEditUserModal', { current: row.original, refreshList: refreshUsers })}
                Icon={PencilSquareIcon}
              />
            </div>
          )
        },
        {
          header: '',
          accessorKey: 'delete',
          cellSize: 1,
          disableSortBy: true,
          cellPad: false,
          cell: ({ row }) => (
            <div className="flex items-center px-1 h-full">
              <Button
                variant="rounded"
                tiny
                onClick={() => NiceModal.show('deleteModal', { name: 'user', handleDelete: () => deleteUser(row.original.UserId) })}
                Icon={TrashIcon}
              />
            </div>
          )
        }
      ]}
    />
  )
}

export default Users